<template>
  <fragment>
    <h1 class="sr-only">Dashboard</h1>
    <div v-if="!hasAppointmentBooked" class="recommendation-upper">
      <div v-if="!recommendations" class="row text-center">
        <Loader style="width: 100%" />
      </div>

      <div v-else-if="recommendations.length === 0" class="row">
        <div class="col-12">
          <div class="">
            <label>RECOMMENDATION</label>
            <h2>There are currently no recommendations</h2>
            <span v-if="userHasPartnerCollege">
              Unfortunately we did not find any counselors that currently match
              your criteria in your area. Please contact us below to request a
              counselor.
            </span>

            <span v-else>
              We’re sorry, but it appears there are no counselors currently
              available based on the parameters selected. If you would like a
              BetterMynd team member to help to coordinate matching you with a
              counselor who is available, please use the contat button below and
              we will get back to you within 24-hours.
            </span>

            <button
              type="button"
              class="btn btn-primary btn-wide d-block mt-4"
              @click="requestCounselor()"
            >
              Contact Us
            </button>
          </div>
        </div>
      </div>

      <div v-else class="row">
        <div
          class="col-12"
          :class="{ 'col-lg-7': recommendations.length === 1 }"
        >
          <label v-if="$route.path === '/dashboard'">RECOMMENDATION</label>

          <label v-else-if="numberOfFreeSessions">
            YOU HAVE {{ numberOfFreeSessions }} SESSION CREDITS REMAINING
          </label>
          <label v-else>CONSULTATION</label>

          <h2 v-if="userHasPartnerCollege">
            <span v-if="userPrefersIndividual">
              50-Minute Individual Session
            </span>
            <span v-else-if="!isFaculty && userPrefersGroup">
              50-Minute Wellbeing Workshop
            </span>
            <span v-else-if="numberOfFreeSessions">
              50-Minute Individual Session
              {{ isFaculty ? '' : 'Or Wellbeing Workshop' }}
            </span>
            <span v-else>
              50-Minute Individual Session
              {{ isFaculty ? '' : 'Or Wellbeing Workshop' }}
            </span>
          </h2>

          <h2 v-else>15-Minute Initial Consult</h2>

          <p v-if="userHasPartnerCollege && userPrefersIndividual && numberOfFreeSessions > 0">
            {{ $store.getters.currentUser.displayName }}, you have
            {{ numberOfFreeSessions }} online therapy sessions covered by your
            school!
          </p>

          <p v-else-if="userHasPartnerCollege && userPrefersGroup && !isFaculty">
            {{ $store.getters.currentUser.displayName }}, you have
            {{ numberOfFreeSessions }} Wellbeing Workshops covered by your school!
          </p>

          <p v-else-if="userHasPartnerCollege && numberOfFreeSessions && numberOfFreeSessions > 0">
            {{ $store.getters.currentUser.displayName }}, you have
            {{ numberOfFreeSessions }} individual sessions
            covered by your school! Schedule an individual
            session with your counselor match
          </p>

          <p v-else-if="userHasPartnerCollege">
            {{ $store.getters.currentUser.displayName }}, review your campus' credit policy and schedule a session!
          </p>

          <div
            v-if="recommendedActions && recommendedActions.recommended_counselor"
            class="match-card"
          >
            <div class="left">
              <div
                v-if="
                  recommendedActions.recommended_counselor &&
                    recommendedActions.recommended_counselor.imageUrl
                "
                role="img"
                class="user-avatar bg-cover"
                aria-label="Counselor's Uploaded Avatar Photo"
                :style="{
                  backgroundImage: `url(${recommendedActions.recommended_counselor.imageUrl})`
                }"
              />
              <div v-else class="user-avatar bg-cover" />
            </div>
            <div class="right">
              <label>COUNSELOR MATCH</label>
              <div class="counselor">
                <router-link
                  :to="`/student/counselor/profile/${recommendedActions.recommended_counselor.id}`"
                >
                  {{ recommendedActions.recommended_counselor.name }}
                </router-link>
              </div>
              <p>
                Next Availability:
                <next-availability :counselor-id="recommendedActions.recommended_counselor.id" />
              </p>
            </div>
          </div>

          <div v-else class="multiple-counselor">
            <div class="row">
              <div
                v-for="counselor in recommendations"
                :key="counselor.id"
                class="col-12 col-lg-4 cursor-pointer"
                @click="selectRecommendation(counselor)"
              >
                <div
                  class="counselor-block"
                  :class="{ active: selectedRecommendation && selectedRecommendation.id === counselor.id }"
                >
                  <div
                    v-if="counselor.imageUrl"
                    class="user-avatar bg-cover"
                    :style="{ backgroundImage: `url(${counselor.imageUrl})` }"
                  />
                  <div v-else class="user-avatar bg-cover" />
                  <div class="name">
                    <router-link :to="`/student/counselor/profile/${counselor.id}`">
                      {{ counselor.firstName }} {{ counselor.lastName }}
                    </router-link>
                  </div>
                  
                  <template>
                    <label>  Next Availability </label>
                    <next-availability :counselor-id="counselor.id" />
                  </template>
                  <label v-if="hasAnyMatchingPreferences(counselor)">
                    MATCHED PREFERENCES
                  </label>
                  <ul>
                    <li
                      v-if="preferences.struggle_areas && preferences.struggle_areas.length > 0"
                      :class="
                        hasAnyMatchingSpecialties(counselor) ? 'active' : 'inactive'
                      "
                    >
                      <font-awesome-icon
                        :icon="[
                          'fas',
                          hasAnyMatchingSpecialties(counselor)
                            ? 'check-circle'
                            : 'times-circle'
                        ]"
                        class="icon alt"
                      />
                      <span>Specialties</span>
                    </li>
                    <li
                      v-if="preferences.gender_preference"
                      :class="
                        userPrefersGender(counselor)
                          ? 'active' : 'inactive'
                      "
                    >
                      <font-awesome-icon
                        :icon="[
                          'fas',
                          userPrefersGender(counselor)
                            ? 'check-circle' : 'times-circle'
                        ]"
                        class="icon alt"
                      />
                      <span>Gender</span>
                    </li>
                    <li
                      v-if="preferences.lgbtq_preference"
                      :class="
                        userPrefersLgbtq(counselor)
                          ? 'active' : 'inactive'
                      "
                    >
                      <font-awesome-icon
                        :icon="[
                          'fas',
                          userPrefersLgbtq(counselor)
                            ? 'check-circle' : 'times-circle'
                        ]"
                        class="icon alt"
                      />
                      <span>LGBTQ+</span>
                    </li>
                    <li
                      v-if="preferences.religion_preference"
                      :class="
                        userPrefersReligion(counselor)
                          ? 'active' : 'inactive'
                      "
                    >
                      <font-awesome-icon
                        :icon="[
                          'fas',
                          userPrefersReligion(counselor)
                            ? 'check-circle' : 'times-circle'
                        ]"
                        class="icon alt"
                      />
                      <span>Religion</span>
                    </li>
                    <li
                      v-if="preferences.language_preference"
                      :class="
                        userPrefersLanguage(counselor)
                          ? 'active' : 'inactive'
                      "
                    >
                      <font-awesome-icon
                        :icon="[
                          'fas',
                          userPrefersLanguage(counselor)
                            ? 'check-circle' : 'times-circle'
                        ]"
                        class="icon alt"
                      />
                      <span>Language</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div class="btn-row">
            <button
              type="button"
              :disabled="!selectedRecommendation"
              class="btn btn-primary btn-wide btn-lg"
              @click="scheduleWithCounselor(selectedRecommendation)"
            >
              Schedule
              <span v-if="selectedRecommendationName">
                with {{ selectedRecommendationName }}
              </span>
            </button>

            <router-link
              v-if="$route.path === '/dashboard/user'"
              to="/counselors"
              tag="button"
              class="btn btn-ghost btn-wide btn-lg"
            >
              View Counselors
            </router-link>

            <router-link
              v-if="!isFaculty && $route.path === '/dashboard/user' && userPrefersGroup"
              to="/workshops"
              tag="button"
              class="btn btn-ghost btn-wide btn-lg"
            >
              View Wellbeing Workshops
            </router-link>
          </div>
        </div>

        <div
          v-if="recommendations.length === 1"
          class="d-none d-lg-block col-lg-5 right-block"
        >
          <div class="vertical-align-md">
            <img
              class="login__image"
              src="@/assets/img/recommendation-img@2x.png"
              alt="background decorative illustration representing counselor search"
            />
          </div>
        </div>
      </div>

      <request-school-modal ref="requestSchoolModal" />
    </div>
  </fragment>
</template>

<script>
import Loader from '@/components/Loader';
import RequestSchoolModal from '@/components/modals/RequestSchoolModal.vue';
import NextAvailability from './components/NextAvailability.vue';

export default {
  name: 'Recommendations',
  components: {
    Loader,
    RequestSchoolModal,
    NextAvailability,
  },
  props: {
    recommendations: {
      type: Array
    },
    scheduleWithCounselor: {
      type: Function
    },
    recommendedActions: {
      type: Object
    },
    selectedRecommendation: {
      type: Object
    },
    hasAppointmentBooked: {
      type: Boolean
    },
    userHasPartnerCollege: {
      type: Boolean
    },
    numberOfFreeSessions: {
      type: Number
    },
    selectedRecommendationName: {
      type: String
    },
    selectRecommendation: {
      type: Function
    }
  },
  data() {
    return {
      isFaculty: false,
    }
  },
  computed: {
    userPrefersIndividual() {
      return (
        this.recommendedActions && this.recommendedActions.hasFreeIndividual
      );
    },
    userPrefersGroup() {
      return this.recommendedActions && this.recommendedActions.hasFreeGroup;
    },
    currentUser() {
      return this.$store.getters.currentUser;
    },
    preferences() {
      return this.currentUser.counseling_preferences;
    }
  },
  async mounted() {
    this.isFaculty = await this.$globals.schoolHasFeature(
      'users-are-faculty',
      this.$store.getters.currentUser.school.id);
  },
  methods: {
    requestCounselor() {
      this.$refs.requestSchoolModal.showCounselorMode();
    },
    userPrefersLanguage(counselor) {
      const user = this.$store.getters.currentUser;
      const language_preference = user.counseling_preferences.language_preference;

      for(const language of counselor.languages)
        if(language.id === language_preference)
          return true;
      return false;
    },

    userPrefersEthnicity(counselor) {
      const user = this.$store.getters.currentUser;
      return user.counseling_preferences.race_preference
          && user.counseling_preferences.race_preference === counselor.ethnicity;
    },

    userPrefersGender(counselor) {
      const user = this.$store.getters.currentUser;
      return user.counseling_preferences.gender_preference
          && user.counseling_preferences.gender_preference === counselor.gender;
    },

    userPrefersReligion(counselor) {
      const user = this.$store.getters.currentUser;
      return user.counseling_preferences.religion_preference
          && user.counseling_preferences.religion_preference === counselor.religion;
    },

    userPrefersLgbtq(counselor) {
      const user = this.$store.getters.currentUser;
      return user.counseling_preferences.lgbtq_preference
          && user.counseling_preferences.lgbtq_preference === true
          && counselor.isLgbtq === true;
    },

    userHasStruggle(specialty) {
      const user = this.$store.getters.currentUser;
      const struggles = user.counseling_preferences.struggle_areas;
      
      for(const struggle of struggles)
        if(struggle.id === specialty.id)
          return true;

      return false;
    },

    hasAnyMatchingPreferences(counselor) {
      return this.userPrefersEthnicity(counselor)
          || this.userPrefersGender(counselor)
          || this.userPrefersReligion(counselor)
          || this.userPrefersLgbtq(counselor)
          || this.userPrefersLanguage(counselor)
          || this.hasAnyMatchingSpecialties(counselor);
    },

    hasAnyMatchingSpecialties(counselor) {
      for(const specialty of counselor.specialties)
        if(this.userHasStruggle(specialty))
          return true;

      return false;
    }
  }
};
</script>

<style lang="scss" scoped>
.recommendation-upper {
  @include border-radius($border-radius);
  @include blue-gradient;
  margin-bottom: 20px;
  padding: 50px;

  h1 {
    margin-bottom: 20px;
  }

  p {
    font-size: 18px;
    margin-bottom: 40px;
  }

  label {
    color: $green;
  }

  .match-card {
    @include border-radius(5px);

    padding: 20px 30px;
    margin-bottom: 40px;
    border: solid 1px white;
    background: rgba(white, 0.05);

    @media (max-width: $md-max) {
      padding: 20px;
    }

    .left {
      width: 130px;
      display: inline-block;
      vertical-align: top;

      @media (max-width: $md-max) {
        width: 100%;
        text-align: center;
      }

      .user-avatar {
        @include background-cover;
        @include border-radius(100px);

        background-image: url('~@/assets/img/default-user.jpg');
        height: 100px;
        width: 100px;

        @media (max-width: $md-max) {
          margin: 0 auto;
        }
      }
    }

    .right {
      width: calc(100% - 130px);
      display: inline-block;
      vertical-align: top;

      @media (max-width: $md-max) {
        width: 100%;
        text-align: center;
      }

      h3 {
        color: white;
        font-size: 28px;
        margin-bottom: 5px;
      }

      .counselor a {
        color: white;
        font-size: 23px;
        margin-bottom: 5px;
        display: block;
        font-weight: 400;
        letter-spacing: 0.05rem;
      }

      p {
        font-size: 16px;
        padding-bottom: 0;
        margin-bottom: 0;
        opacity: 0.75;
        font-weight: normal;
      }
    }
  }

  .multiple-counselor {
    margin-bottom: 15px;

    .counselor-block {
      @include border-radius(5px);

      border: solid 1px white;
      margin-bottom: 30px;
      padding: 30px;

      .name {
        display: block;
        border-bottom: solid 1px rgba(white, 0.2);
        padding-bottom: 15px;
        margin-bottom: 15px;

        a {
          color: white;
          font-weight: 500;
          letter-spacing: 0.05rem;
          word-break: break-word;
          font-size: 1.1rem;
        }
      }

      .user-avatar {
        @include background-cover;
        @include border-radius(100px);

        background-image: url('~@/assets/img/default-user.jpg');
        height: 85px;
        width: 85px;
        position: relative;
        top: 2px;
        margin-bottom: 15px;

        @media (max-width: $md-max) {
          margin: 0 auto;
        }
      }

      label {
        font-size: 12px;
        letter-spacing: 0.1rem;
        text-transform: uppercase;
        margin-bottom: 0;
        color: white;
      }

      ul {
        list-style-type: none;
        padding-left: 0;
        margin-top: 10px;

        li {
          font-size: 18px;
          opacity: 0.5;

          &.active {
            opacity: 1;
          }

          i,
          svg {
            color: $green;
            margin-right: 10px;
          }
        }
      }

      &.active {
        background: $blue;
        border-color: $blue;
      }
    }
  }

  .btn-row {
    .btn {
      margin-right: 10px;

      @media (max-width: $md-max) {
        margin-right: 0;
        margin-bottom: 10px;
        width: 100%;
      }
    }
  }

  .right-block {
    text-align: right;

    img {
      height: auto;
      width: 100%;
      max-width: 450px;
      max-height: 100%;
      max-height: 440px;
    }
  }
}

.counselor-block {
  h3 {
    a {
      color: white;

      &:hover {
        text-decoration: none;
        color: $green;
      }
    }
  }
}
</style>
